// @flow
import React from 'react';
import { Link } from 'react-router';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import Toggle from 'react-toggle';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

type Params = {
  userId: number,
  organisationId: number,
  context: string
};

type Props = {
  params: Params,
  currentUser: Object,
  currentOrganisation: Object,
  getAllocatedUserOrganisations: (u: number, o: number) => void,
  getUnallocatedUserOrganisations: (u: number, o: number) => void,
  getUserLocations: (u: number) => Promise<*>,
  updateUserOrganisation: (
    u: number,
    o: number,
    a: boolean,
    u?: Object
  ) => Promise<*>,
  addUserOrganisation: (
    u: number,
    o: number,
    ni: ?number,
    nn: ?string
  ) => Promise<*>
};

type State = {
  newSubAccountId: ?any,
  newSubAccountName?: any,
  showNewSubAccount: boolean,
  filterValue: string,
  isSaving?: boolean,
  errorMsg?: string,
  successMsg?: string
};

const ControlHeader = props => (
  <Row className="row-header tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      {props.context === 'customer' && (
        <Button
          bsStyle="default"
          type="submit"
          className="relative-padding pull-right"
          onClick={props.assignButtonClick}
        >
          Assign
        </Button>
      )}
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="User Sub-Account Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const {
    item,
    removeUserOrganisation,
    organisationId,
    updateIsAdmin,
    currentUser,
    context
  } = props;
  const isAdminItem: boolean = item.permissions.some(
    p => p.permissionName === 'Admin'
  );
  const isAdminUser = !!currentUser.permissions.find(
    p => p.permissionName === 'Admin'
  );

  const isAdmin = isAdminItem || isAdminUser;

  // TODO: Disable admin toggle if is admin of parent account
  //       but not of child and is accessing via sub-account.
  if (context === 'global') {
    return (
      <Row key={item.organisationId}>
        <Col sm={3} className="table-entry">
          <Link
            to={`/customer/${item.parentId}/sub-accounts/${
              item.organisationId
            }`}
          >
            {item.organisationName}
          </Link>
        </Col>
        <Col sm={3} className="table-entry text-center">
          <Link to={`/customers/${item.parentId}`}>{item.parentName}</Link>
        </Col>
        <Col sm={1} className="table-entry text-center">
          {formatDate(item.dateCreated, true)}
        </Col>
        <Col sm={1} className="table-entry text-center">
          {formatDate(item.dateUpdated, true)}
        </Col>
        <Col sm={2} className="table-entry text-center">
          {item.permissions.length > 1 ? 'Yes' : 'No'}
        </Col>
        <Col sm={2} className="table-entry text-center">
          {item.userOrganisationId && (
            <Button
              bsStyle="link"
              onClick={e => removeUserOrganisation(item)}
              aria-label={`Remove ${item.organisationName}`}
            >
              Remove
            </Button>
          )}
        </Col>
      </Row>
    );
  } else {
    return (
      <Row key={item.organisationId}>
        <Col sm={6} className="table-entry">
          <Link
            to={`/customer/${organisationId}/sub-accounts/${
              item.organisationId
            }`}
          >
            {item.organisationName}
          </Link>
        </Col>
        <Col sm={3} className="table-entry text-center">
          <Toggle
            checked={isAdmin}
            className="primary-color"
            id="isAdmin"
            icons={false}
            onChange={e => updateIsAdmin(e.target.checked, item)}
            disabled={!item.userOrganisationId}
          />
        </Col>
        <Col sm={3} className="table-entry text-center">
          {item.userOrganisationId && (
            <Button
              bsStyle="link"
              onClick={e => removeUserOrganisation(item)}
              aria-label={`Remove ${item.organisationName}`}
            >
              Remove
            </Button>
          )}
        </Col>
      </Row>
    );
  }
};

class UserAccounts extends React.Component<Props, State> {
  fuzzyFilterFactory: any;
  timeoutId: any;
  screenReaderContent: any;
  static displayName = 'UserAccounts';

  constructor(props: Props) {
    super(props);

    this.state = {
      newSubAccountId: undefined,
      showNewSubAccount: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.getUserOrganisations();
    this.screenReaderContent.focus();

    if (this.props.params.context === 'global') {
      this.props.getUserLocations(this.props.currentUser.id);
    }
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getUserOrganisations = () => {
    const userId = parseInt(this.props.params.userId, 10);
    const { params } = this.props;

    const organisationId = parseInt(this.props.params.organisationId, 10);

    if (params.context === 'global') {
      this.props.getUserLocations(userId);
    } else {
      this.props.getAllocatedUserOrganisations(userId, organisationId);
      this.props.getUnallocatedUserOrganisations(userId, organisationId);
    }
  };

  removeUserOrganisation = (item: Object) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const { userOrganisationId, parentId, organisationId } = item;
    const isArchived = true;

    const { context } = this.props.params;
    const parentOrOrganisationId =
      context === 'global' ? organisationId : parentId;

    this.props
      .updateUserOrganisation(
        userOrganisationId,
        parentOrOrganisationId,
        isArchived
      )
      .then(
        response => {
          this.getUserOrganisations();
          this.success(response);
        },
        error => this.failure(error)
      );
  };

  updateIsAdmin = (isAdmin: boolean, item: Object) => {
    const { userOrganisationId, permissions, isArchived, parentId } = item;
    let updatedPermissions = [...permissions];
    if (isAdmin) {
      // add admin permissions
      const adminPermissionId = 2;
      updatedPermissions.push({
        permissionId: adminPermissionId,
        permissionName: 'Admin'
      });
    } else {
      // filter out the admin permission
      updatedPermissions = permissions.filter(
        p => p.permissionName !== 'Admin'
      );
    }
    this.props
      .updateUserOrganisation(
        userOrganisationId,
        parentId,
        isArchived,
        updatedPermissions
      )
      .then(
        response => {
          this.getUserOrganisations();
          this.success(response);
        },
        error => console.log('Error toggling admin status of userOrganisation')
      );
  };

  assignButtonClick = () => {
    this.setState({ showNewSubAccount: !this.state.showNewSubAccount });
  };

  selectOnChange = (newSubAccountId: number, newSubAccountName: string) => {
    this.setState({ newSubAccountId, newSubAccountName });
  };

  addSubAccount = () => {
    if (this.state.newSubAccountId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const { newSubAccountId, newSubAccountName } = this.state;
      const { currentUser, currentOrganisation } = this.props;

      const organisationId = currentOrganisation
        ? currentOrganisation.id
        : currentUser.organisationId;

      this.props
        .addUserOrganisation(
          currentUser.id,
          organisationId,
          newSubAccountId,
          newSubAccountName
        )
        .then(
          response => {
            this.getUserOrganisations();
            this.success(response);
          },
          error => this.failure(error)
        );
    }
    this.setState({ showNewSubAccount: false, newSubAccountId: undefined });
  };

  success = (response: Object) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  cancel = () => {
    this.setState({ showNewSubAccount: false, newSubAccountId: undefined });
  };

  updateFilterValue = (filterValue: string) => {
    this.setState({ filterValue: filterValue || '' });
  };

  render() {
    const {
      currentUser,
      currentUser: {
        organisations: { unallocated }
      },
      params
    } = this.props;

    const allocated =
      params.context === 'global'
        ? currentUser.locations
        : currentUser.organisations.allocated;

    const LineItemWrapper = props => (
      <LineItem
        {...props}
        organisationId={params.organisationId}
        removeUserOrganisation={this.removeUserOrganisation}
        updateIsAdmin={this.updateIsAdmin}
        currentUser={this.props.currentUser}
        context={params.context}
      />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing accounts for {this.props.currentUser.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          assignButtonClick={this.assignButtonClick}
          context={params.context}
        />
        {this.props.params.context === 'global' ? (
          <Row>
            <Col sm={3} className="table-header">
              Sub-Account Name
            </Col>
            <Col sm={3} className="table-header text-center">
              Customer
            </Col>
            <Col sm={1} className="table-header text-center">
              Created
            </Col>
            <Col sm={1} className="table-header text-center">
              Updated
            </Col>
            <Col sm={2} className="table-header text-center">
              Admin
            </Col>
            <Col sm={2} className="table-header text-center">
              Actions
            </Col>
          </Row>
        ) : (
          <Row>
            <Col sm={6} className="table-header">
              Sub-Account Name
            </Col>
            <Col sm={3} className="table-header text-center">
              Administrator
            </Col>
            <Col sm={3} className="table-header text-center">
              Actions
            </Col>
          </Row>
        )}
        <hr />

        {this.state.showNewSubAccount && (
          <AddNewRow
            items={unallocated}
            addItem={this.addSubAccount}
            selectValue={this.state.newSubAccountId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="sub-accounts"
          keys={['organisationId', 'organisationName']}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default UserAccounts;



// WEBPACK FOOTER //
// ./src/containers/users/UserAccounts.js